import { useEffect, useRef } from 'react';

type MiniWindowsMap = Record<string, Window | null>;

const defaultSize = { width: 1250, height: 1647 };

const useMiniWindow = () => {
  const miniWindowsRef = useRef<MiniWindowsMap>({});

  // Clean up dead references on unload
  useEffect(() => {
    const onUnload = () => {
      Object.values(miniWindowsRef.current).forEach((w) => {
        try {
          if (w && !w.closed) w.close();
        } catch {
          /* ignore */
        }
      });
    };
    window.addEventListener('beforeunload', onUnload);
    return () => window.removeEventListener('beforeunload', onUnload);
  }, []);

  const exists = (name: string): boolean => !!miniWindowsRef.current[name] && !miniWindowsRef.current[name]!.closed;

  const getWindow = (name: string): Window | null => (exists(name) ? miniWindowsRef.current[name]! : null);

  const close = (name: string): boolean => {
    if (exists(name)) {
      miniWindowsRef.current[name]!.close();
      miniWindowsRef.current[name] = null;
      return true;
    }
    return false;
  };

  const setTitle = (name: string, title: string) => {
    const w = getWindow(name);
    if (!w) return false;
    try {
      // Same-origin only; will throw on cross-origin
      w.document.title = title;
      return true;
    } catch {
      return false;
    }
  };

  const open = (
    url: string | URL | undefined,
    name = 'miniWindow',
    width = defaultSize.width,
    height = defaultSize.height,
    replace = false,
    desiredTitle?: string,
    features?: any,
    keepCentered = true,
    focus = true
  ): Window | null => {
    if (replace && exists(name)) close(name);

    const left = keepCentered ? Math.max(0, window.screen.width / 2 - width / 2) : 0;
    const top = keepCentered ? Math.max(0, window.screen.height / 2 - height / 2) : 0;

    const baseFeatures = `location=yes,scrollbars=yes,status=yes,width=${width},height=${height},left=${left},top=${top}`;
    const feat = features ? `${baseFeatures},${features}` : baseFeatures;

    // Reuse existing named window if present
    const w = window.open(url, name, feat) ?? null;
    miniWindowsRef.current[name] = w;

    if (!w) return null; // blocked by popup blocker

    // Try to set the title after load (same-origin only)
    if (desiredTitle) {
      const trySet = () => {
        try {
          w.document.title = desiredTitle;
        } catch {
          // Cross-origin; cannot set title here
        }
      };
      // If already loaded:
      try {
        if (w.document?.readyState === 'complete') trySet();
      } catch {}
      // Also attach onload:
      try {
        w.addEventListener?.('load', trySet);
      } catch {}
    }

    if (focus) {
      try {
        w.focus();
      } catch {}
    }

    return w;
  };

  return { miniWindow: miniWindowsRef, open, exists, getWindow, close, setTitle };
};

export default useMiniWindow;
